import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Copy, Check, Share2, ExternalLink, Sprout } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchHarvestSummary, harvestCardUrl, harvestShareOgUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function HarvestParty() {
    const { token } = useParams();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchHarvestSummary(token)
            .then(d => { if (!cancelled) setSummary(d); })
            .catch(e => { if (!cancelled) setError(formatApiError(e)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [token]);

    const cardUrl = harvestCardUrl(token);
    const ogUrl = harvestShareOgUrl(token);

    const copyOg = async () => {
        try {
            await navigator.clipboard.writeText(ogUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
            toast.success("Share link copied");
        } catch {
            toast.error("Could not copy — copy manually");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--vm-bg)] p-10">
                <p className="text-[var(--vm-text-muted)]" data-testid="harvest-loading">
                    Plating up your harvest…
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--vm-bg)] p-10">
                <div className="journal-card p-8 max-w-md text-center" data-testid="harvest-error">
                    <Sprout className="w-10 h-10 text-[var(--vm-accent)] mx-auto mb-3" />
                    <h1 className="font-serif-display text-3xl text-[var(--vm-text)] mb-2">
                        This garden isn't sharing
                    </h1>
                    <p className="text-sm text-[var(--vm-text-muted)]">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--vm-bg)] py-16 px-6" data-testid="harvest-party-page">
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-10">
                    <p className="text-xs uppercase tracking-[0.2em] text-[var(--vm-sage)] mb-2">
                        Harvest Party · rooteed
                    </p>
                    <h1
                        className="font-serif-display text-5xl lg:text-6xl text-[var(--vm-text)]"
                        data-testid="harvest-garden-name"
                    >
                        {summary.garden_name}
                    </h1>
                    <p className="mt-3 text-sm text-[var(--vm-text-muted)]">
                        {summary.owner_name}
                        {summary.location ? ` · ${summary.location}` : ""}
                    </p>
                </div>

                <div className="journal-card overflow-hidden mb-8 border border-[var(--vm-border)]">
                    <img
                        src={cardUrl}
                        alt={`${summary.garden_name} — harvest card`}
                        data-testid="harvest-card-image"
                        className="w-full block"
                        loading="eager"
                    />
                </div>

                <div className="grid sm:grid-cols-3 gap-4 mb-10">
                    <div className="journal-card p-5" data-testid="harvest-stat-total">
                        <div className="text-xs uppercase tracking-wider text-[var(--vm-sage)] mb-1">Harvested</div>
                        <div className="font-serif-display text-4xl text-[var(--vm-terracotta)]">
                            {summary.total_kg.toFixed(1)} <span className="text-lg text-[var(--vm-text-muted)]">kg</span>
                        </div>
                    </div>
                    <div className="journal-card p-5" data-testid="harvest-stat-events">
                        <div className="text-xs uppercase tracking-wider text-[var(--vm-sage)] mb-1">Events</div>
                        <div className="font-serif-display text-4xl text-[var(--vm-text)]">{summary.events}</div>
                    </div>
                    <div className="journal-card p-5" data-testid="harvest-stat-crops">
                        <div className="text-xs uppercase tracking-wider text-[var(--vm-sage)] mb-1">Unique crops</div>
                        <div className="font-serif-display text-4xl text-[var(--vm-text)]">{summary.unique_crops}</div>
                    </div>
                </div>

                {summary.top_crops && summary.top_crops.length > 0 && (
                    <div className="journal-card p-6 mb-8" data-testid="harvest-top-crops">
                        <h2 className="font-serif-display text-2xl text-[var(--vm-text)] mb-4">Top crops</h2>
                        <ul className="divide-y divide-[var(--vm-border)]">
                            {summary.top_crops.map((c) => (
                                <li
                                    key={c.name}
                                    className="flex items-center justify-between py-2"
                                    data-testid={`harvest-top-crop-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <span className="text-[var(--vm-text)]">{c.name}</span>
                                    <span className="text-[var(--vm-text-muted)] font-mono">{c.kg.toFixed(2)} kg</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="flex flex-wrap justify-center gap-3">
                    <Button
                        asChild
                        data-testid="harvest-download-png-button"
                        className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                    >
                        <a href={cardUrl} download={`${summary.garden_name || "harvest"}-card.png`}>
                            <Download className="w-4 h-4 mr-2" /> Download PNG
                        </a>
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={copyOg}
                        data-testid="harvest-copy-link-button"
                        className="rounded-full border-[var(--vm-border)]"
                    >
                        {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                        {copied ? "Copied" : "Copy share link"}
                    </Button>
                    <Button
                        asChild
                        variant="ghost"
                        data-testid="harvest-visit-garden-button"
                        className="rounded-full text-[var(--vm-text-muted)]"
                    >
                        <a href={`/share/${token}`}>
                            <ExternalLink className="w-4 h-4 mr-2" /> Visit the garden
                        </a>
                    </Button>
                </div>

                <p className="text-center text-xs text-[var(--vm-text-muted)] mt-12">
                    Made with rooteed · plan, grow, celebrate your garden
                </p>
            </div>
        </div>
    );
}
